<template>
  <div>
    <v-toolbar color="primary" dark tabs flat dense class="view-toolbar">
      <v-tooltip v-if="!drawerState" bottom>
        <template #activator="{ on }">
          <v-app-bar-nav-icon @click="$emit('toggle-drawer')" v-on="on"></v-app-bar-nav-icon>
        </template>
        <span>Open side menu</span>
      </v-tooltip>
      <v-toolbar-title class="ml-2">{{ toolbarTitle }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon :disabled="!hasChanged" @click="$emit('save')">
        <v-icon>save</v-icon>
      </v-btn>
    </v-toolbar>

    <CaseVisualization
      v-if="hash === 'NetworkDiagram'"
      :current-case="currentCase"
      @save="$emit('save')"
      @change="$emit('change')"
    ></CaseVisualization>

    <template v-if="hash === 'Map'">
      <v-toolbar v-if="layersItems.length" flat>
        <v-col sm="4" class="mt-8">
          <v-select
            v-model="selectedLayers"
            label="Projects Layers"
            :items="layersItems"
            multiple
            chips
            :disabled="!layersItems.length"
            item-text="text"
            return-object
            dense
            placeholder="All Projects"
          >
            <template #selection="data">
              <v-chip
                :key="JSON.stringify(data.item)"
                v-bind="data.attrs"
                :input-value="data.selected"
                :disabled="data.disabled"
                :color="data.item.color"
                small
                @click:close="data.parent.selectItem(data.item)"
              >
                {{ data.item.text }}
              </v-chip>
            </template>
          </v-select>
        </v-col>
      </v-toolbar>

      <VisGeo
        :case-id="case_id"
        :case-name="case_name"
        :projects="projects"
        :nodes="nodes"
        :branches="branches"
        :outputs="SSEYearlyOutput"
        :map-layers="mapLayers"
        :start-year="this.currentCase ? this.currentCase.settings.year_start : ''"
        :offsetTop="layersItems.length"
        @add-map-layer="addMapLayer"
        @rename-map-layer="renameMapLayer"
        @remove-map-layer="removeMapLayer"
      />
    </template>
  </div>
</template>

<script>
import { makeFindMixin } from "feathers-vuex";
import { DateTime } from "luxon";
import initSqlJs from "sql.js";
// eslint-disable-next-line import/no-webpack-loader-syntax
import sqlWasm from "!!file-loader?name=sql-wasm-[contenthash].wasm!sql.js/dist/sql-wasm.wasm";

export default {
  name: "CaseVis",
  components: {
    VisGeo: () => import("@/components/VisGeo.vue"),
    CaseVisualization: () => import("@/views/CaseVisualization.vue"),
  },
  mixins: [
    makeFindMixin({
      service: "outputs",
      queryWhen: true,
      watch: "params",
      fetchParams: "outputsFetchParams",
    }),
  ],
  props: {
    currentCase: Object,
    num_years: Number,
    hasChanged: Boolean,
    drawerState: { type: Boolean, required: false, default: true },
  },
  data() {
    return {
      tabRef: 0,
      selectedLayers: [],
      dbSql: null,
      sqlSSEOutput: [],
    };
  },
  computed: {
    pages() {
      return {
        Map: {
          title: "Map",
        },
        NetworkDiagram: {
          title: "Case Visualization (beta)",
        },
      };
    },
    hash() {
      return this.$route.hash.substring(1);
    },
    page() {
      return this.pages[this.hash];
    },
    toolbarTitle() {
      return this.page.title;
    },
    case_id() {
      return this.currentCase ? this.currentCase.id : null;
    },
    case_name() {
      return this.currentCase ? this.currentCase.name : "";
    },
    outputsParams() {
      return {
        query: {
          case_id: this.case_id,
        },
      };
    },
    outputsFetchParams() {
      return {
        query: {
          case_id: this.case_id,
          $select: ["id", "case_id", "output", "output_sql", "output_url"],
        },
      };
    },
    output() {
      return this.outputs.reduce((a, e) => {
        const res = a;
        let { output } = e;
        if (!output && e.output_sql) {
          if (e.type_name === "STACKED_SERVICES_EMULATOR") {
            output = this.sqlSSEOutput;
          }
        }
        res[e.type_name] = output;
        return res;
      }, {});
    },
    SSEYearlyOutput() {
      const outputs = this.output.STACKED_SERVICES_EMULATOR;
      if (!outputs || !outputs.length) {
        return {};
      }

      const filteredOutputs = outputs.filter(
        (project) =>
          project &&
          this.isProjectSelected(project) &&
          project[Object.keys(project)[0]].yearlyResults
      );
      if (!filteredOutputs.length) {
        return {};
      }

      // Use actual years from the data (startDate year, not year_start)
      const firstProject = filteredOutputs[0];
      const availableYears = Object.keys(firstProject[Object.keys(firstProject)[0]].yearlyResults)
        .map(Number)
        .sort();

      return availableYears.reduce(
        (acc, year) => ({
          ...acc,
          [year]: filteredOutputs.map((project) => ({
            name: Object.keys(project)[0],
            results: project[Object.keys(project)[0]].yearlyResults[year],
          })),
        }),
        {}
      );
    },
    nodes() {
      if (this.currentCase && this.currentCase.input && this.currentCase.input.nodes) {
        return this.currentCase.input.nodes;
      }
      return [];
    },
    branches() {
      if (this.currentCase && this.currentCase.input && this.currentCase.input.branches) {
        return this.currentCase.input.branches;
      }
      return [];
    },
    mapLayers() {
      return this.currentCase && this.currentCase.map_layers ? this.currentCase.map_layers : [];
    },
    layersItems() {
      const layers =
        this.currentCase && this.currentCase.input.projects.length
          ? this.currentCase.input.projects
              .filter((p) => (p.coordinates ? p.coordinates.every((c) => c) : false) && p.layer)
              .map((p) => p.layer)
          : [];
      const distinctLayers = [...new Set(layers.map((l) => l.text))].map((layerName) => ({
        text: layerName,
        color: layers.find((l) => l.text === layerName).color,
      }));
      return distinctLayers.length ? distinctLayers : [];
    },
    projects() {
      return this.currentCase && this.currentCase.input.projects.length
        ? this.currentCase.input.projects
            .filter((p) => (p.coordinates ? p.coordinates.every((c) => c) : false))
            .filter((p) =>
              p.layer && this.selectedLayers.length
                ? this.selectedLayers.map((l) => l.text).includes(p.layer.text)
                : true
            )
            .flatMap((project) => {
              if (false) {
                const points = [
                  {
                    project: project.name,
                    name: project.name,
                    coordinates: project.coordinates,
                    iconType: "",
                  },
                ];
                if (project.storages.length) {
                  points.push(
                    project.storages
                      .filter((s) => (s.coordinates ? s.coordinates.every((c) => c) : false))
                      .map((s) => ({
                        project: project.name,
                        name: `[${project.name}] ${s.name}`,
                        coordinates: s.coordinates,
                        iconType: "storage",
                      }))
                  );
                }
                if (project.generators.length) {
                  points.push(
                    project.generators
                      .filter((g) => (g.coordinates ? g.coordinates.every((c) => c) : false))
                      .map((g) => ({
                        project: project.name,
                        name: `[${project.name}] ${g.name}`,
                        coordinates: g.coordinates,
                        iconType: this.getGeneratorIcon(g),
                      }))
                  );
                }
                return points.flat();
              }
              return {
                project: project.name,
                name: project.name,
                coordinates: project.coordinates,
                iconType: this.getProjectIcon(project),
                color: project.layer ? project.layer.color : "",
              };
            })
        : [];
    },
    loadedProjects() {
      return Boolean(this.currentCase && this.currentCase.input.projects.length);
    },
  },
  watch: {
    loadedProjects(val) {
      if (val) this.selectedLayers = this.layersItems;
    },
    currentCase(val) {
      if (val) this.selectedLayers = this.layersItems;
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {
    // Clean up database connection to prevent memory leaks
    if (this.dbSql) {
      this.dbSql.close();
      this.dbSql = null;
    }
  },
  destroyed() {},
  methods: {
    // Patch only map_layers on the case on the model to circumvent output wipeout on
    // map layer updates (which is only a visualization change)
    persistMapLayers(previousMapLayers) {
      return this.$store
        .dispatch("cases/patch", [
          this.currentCase.id,
          { map_layers: this.currentCase.map_layers },
          {},
        ])
        .catch((error) => {
          this.currentCase.map_layers = previousMapLayers;
          console.error("Failed to save map layers", error);
        });
    },
    addMapLayer(layer) {
      const previous = this.currentCase.map_layers ? [...this.currentCase.map_layers] : [];
      if (!this.currentCase.map_layers) {
        this.currentCase.map_layers = [];
      }
      this.currentCase.map_layers.push(layer);
      this.persistMapLayers(previous);
    },
    renameMapLayer({ id, name }) {
      const layers = this.currentCase.map_layers || [];
      const previous = [...layers];
      const layer = layers.find((l) => l.id === id);
      if (!layer) return;
      layer.name = name;
      this.persistMapLayers(previous);
    },
    removeMapLayer(layerId) {
      const previous = this.currentCase.map_layers ? [...this.currentCase.map_layers] : [];
      this.currentCase.map_layers = previous.filter((l) => l.id !== layerId);
      this.persistMapLayers(previous);
    },
    getProjectIcon(project) {
      if (project.storages.length) {
        return "storage";
      }
      if (project.generators.length) {
        if (project.generators[0].type === "SOLAR_PV") return "solarPv";
        if (project.generators[0].type === "WIND") return "wind";
        if (project.generators[0].type === "THERMAL") return "thermal";
        if (project.generators[0].type === "HYDRO") return "hydro";
      }
      return "";
    },
    getGeneratorIcon(generator) {
      if (generator.type === "THERMAL") return "thermal";
      if (generator.type === "RENEWABLE") {
        if (generator.source === "SOLAR_PV") return "solarPv";
        if (generator.source === "WIND") return "wind";
        if (generator.source === "HYDRO") return "hydro";
      }
      return "";
    },
    isProjectSelected(project) {
      const projectName = Object.keys(project)[0];
      return this.projects.map((p) => p.name).includes(projectName);
    },
    parseSSESqlOutputs() {
      const sqlOutput = this.outputs.find(
        (v) => v.type_name === "STACKED_SERVICES_EMULATOR"
      ).output_sql;
      initSqlJs({ locateFile: () => sqlWasm }).then((SQL) => {
        this.dbSql = new SQL.Database(Buffer.from(sqlOutput.data));
        const [response] = this.dbSql.exec("SELECT * FROM PROJECTS");
        if (response) {
          const { columns } = response;
          const projects = response.values;
          projects.forEach((project) => {
            const projectObj = columns.reduce(
              (acc, key, index) => ({ ...acc, [key]: project[index] }),
              {}
            );
            // Yearly Results
            projectObj.yearlyResults = this.parseSQLyearly(projectObj.id);
            this.sqlSSEOutput.push({ [projectObj.name]: Object.freeze(projectObj) });
          });
        }
        // Keep database connection open for lazy loading - will be closed in beforeDestroy
      });
    },
    parseSQLyearly(projectId) {
      const parsed = {};
      ["markets", "storages", "demands", "generators"].forEach((resource) => {
        const response = this.dbSql.exec(
          `SELECT * FROM 'yearly_${resource}' where projectID = ${projectId}`
        );
        if (response[0]) {
          const objectKeys = response[0].columns;
          response[0].values.forEach((row) => {
            const rowObject = this.sqlToObject(objectKeys, row);
            const { year, name: resourceName } = rowObject;
            if (!parsed[year]) {
              parsed[year] = {
                markets: {},
                storages: {},
                demands: {},
                generators: {},
              };
            }
            if (!parsed[year][resource][resourceName]) {
              parsed[year][resource][resourceName] = rowObject;
            }
          });
        }
      });
      return parsed;
    },
    sqlToObject(columns, row) {
      return columns.reduce((acc, key, index) => {
        const fixedKey = key === "date" ? "datetime" : key;
        let value = row[index];
        if (fixedKey === "datetime") {
          value = DateTime.fromISO(value.replace(" ", "T"), { zone: "UTC" }).toISO();
        }
        return { ...acc, [fixedKey]: value };
      }, {});
    },
  },
};
</script>

<style></style>
